/**
 * DRI Charts: the 4 charts at the top of DRI Insights.
 * Receives only raw data; all derivation and chart-specific state live here.
 * - Employee Tier Distribution (EmployeeTierDistributionCard)
 * - Employees by Tier (EmployeesByTierBarChart)
 * - Average DRI Scores by Product (AverageDRIByProductBarChart)
 * - Weekly DRI Trends by Product (WeeklyDRITrendsChart)
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import { startOfWeek, endOfWeek } from 'date-fns';
import { EmployeesByTierBarChart, AverageDRIByProductBarChart, WeeklyDRITrendsChart, getTopNProductsByLatestWeek } from '../charts';
import EmployeeTierDistributionCard from './EmployeeTierDistributionCard';
import type { UserDRIInsight, EmployeeSummary } from '../../types/driInsights';
import {
  getBestTierForEmployee,
  computeAvailableBUsFromEmployeeSummary,
  computeAvailableProductsForTierDistribution,
  computeEmployeesByTierData,
  computeProductDriDataAll,
  computeFilteredTierDistribution,
  computeProductDriDataFilteredByBUs,
} from '../../utils/driInsightsUtils';
import type { WeeklyTrendRow } from '../charts/WeeklyDRITrendsChart';

export interface DRIChartsProps {
  /** Filtered DRI data (product-filtered users with dri_details). */
  filteredData: UserDRIInsight[];
  /** Employee summary (one per user with product_details). */
  employeeSummaryData: EmployeeSummary[];
  /** Raw weekly trends from API. */
  weeklyTrendsRaw: WeeklyTrendRow[];
  /** Available product names from weekly trends API. */
  availableProductsFromApi: string[];
  /** Loading flag for weekly trends. */
  isLoadingWeeklyTrends: boolean;
}

const DRICharts: React.FC<DRIChartsProps> = ({
  filteredData,
  employeeSummaryData,
  weeklyTrendsRaw,
  availableProductsFromApi,
  isLoadingWeeklyTrends,
}) => {
  // --- Employees by Tier ---
  const [employeesByTierViewMode, setEmployeesByTierViewMode] = useState<'product' | 'bu'>('product');
  const [legendAnchorEl, setLegendAnchorEl] = useState<HTMLElement | null>(null);

  // --- Average DRI Bar Chart ---
  const [selectedBUsForBarGraph, setSelectedBUsForBarGraph] = useState<string[]>([]);
  const [pendingBUsForBarGraph, setPendingBUsForBarGraph] = useState<string[]>([]);
  const [buFilterMenuAnchor, setBuFilterMenuAnchor] = useState<HTMLElement | null>(null);
  const hasBeenManuallyClearedBarGraphRef = useRef(false);
  const hasInitializedBarGraphRef = useRef(false);

  // --- Weekly DRI Trends ---
  const [selectedProductsForTrendChart, setSelectedProductsForTrendChart] = useState<string[]>([]);
  const [pendingProductsForTrendChart, setPendingProductsForTrendChart] = useState<string[]>([]);
  const [productFilterMenuAnchor, setProductFilterMenuAnchor] = useState<HTMLElement | null>(null);
  const [trendChartStartDate, setTrendChartStartDate] = useState<Date | null>(() =>
    startOfWeek(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), { weekStartsOn: 1 })
  );
  const [trendChartEndDate, setTrendChartEndDate] = useState<Date | null>(() =>
    endOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [dateRangeMenuAnchor, setDateRangeMenuAnchor] = useState<HTMLElement | null>(null);
  const [dateRangeTab, setDateRangeTab] = useState(0);
  const [hoveredDataPoints, setHoveredDataPoints] = useState<Set<string>>(new Set());
  const [tooltipState, setTooltipState] = useState<{
    x: number;
    y: number;
    week: string;
    entries: Array<{ name: string; value: number; color: string }>;
  } | null>(null);
  const hasBeenManuallyClearedRef = useRef(false);
  /** Bumps when weekly rows or API product list change — drives default “top 5 by latest week” selection. */
  const lastTrendChartInitKeyRef = useRef<string>('');

  // --- Tier Distribution Card ---
  const [selectedBUsForTierDistribution, setSelectedBUsForTierDistribution] = useState<string[]>([]);
  const [pendingBUsForTierDistribution, setPendingBUsForTierDistribution] = useState<string[]>([]);
  const [buTierDistributionMenuAnchor, setBuTierDistributionMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedProductsForTierDistribution, setSelectedProductsForTierDistribution] = useState<string[]>([]);
  const [pendingProductsForTierDistribution, setPendingProductsForTierDistribution] = useState<string[]>([]);
  const [productTierDistributionMenuAnchor, setProductTierDistributionMenuAnchor] = useState<HTMLElement | null>(null);
  const hasBeenManuallyClearedTierDistributionRef = useRef(false);
  const hasInitializedTierDistributionRef = useRef(false);
  const hasBeenManuallyClearedTierDistributionProductsRef = useRef(false);
  const hasUserManuallySelectedProductsRef = useRef(false);
  const hasInitializedProductsForTierDistributionRef = useRef(false);

  // Derived: available BUs and products for filters (from employeeSummaryData)
  const availableBUsForBarGraph = useMemo(
    () => computeAvailableBUsFromEmployeeSummary(employeeSummaryData),
    [employeeSummaryData]
  );
  const availableBUsForTierDistribution = useMemo(
    () => computeAvailableBUsFromEmployeeSummary(employeeSummaryData),
    [employeeSummaryData]
  );
  const availableProductsForTierDistribution = useMemo(
    () => computeAvailableProductsForTierDistribution(employeeSummaryData),
    [employeeSummaryData]
  );

  const productDriDataAll = useMemo(() => computeProductDriDataAll(filteredData), [filteredData]);
  const employeesByTierData = useMemo(
    () => computeEmployeesByTierData(filteredData, employeesByTierViewMode),
    [filteredData, employeesByTierViewMode]
  );
  const filteredTierDistribution = useMemo(
    () =>
      computeFilteredTierDistribution(
        employeeSummaryData,
        selectedBUsForTierDistribution,
        selectedProductsForTierDistribution
      ),
    [employeeSummaryData, selectedBUsForTierDistribution, selectedProductsForTierDistribution]
  );
  const productDriData = useMemo(
    () => computeProductDriDataFilteredByBUs(employeeSummaryData, selectedBUsForBarGraph),
    [employeeSummaryData, selectedBUsForBarGraph]
  );

  // Reset init flags when DRI data length changes
  const prevEmployeeSummaryDataLengthRef = useRef<number>(0);
  useEffect(() => {
    const currentLength = employeeSummaryData?.length || 0;
    const prevLength = prevEmployeeSummaryDataLengthRef.current;
    if (currentLength !== prevLength && prevLength > 0) {
      if (!hasBeenManuallyClearedTierDistributionRef.current) hasInitializedTierDistributionRef.current = false;
      if (!hasBeenManuallyClearedTierDistributionProductsRef.current && !hasUserManuallySelectedProductsRef.current) {
        hasInitializedProductsForTierDistributionRef.current = false;
      }
      if (!hasBeenManuallyClearedBarGraphRef.current) hasInitializedBarGraphRef.current = false;
    }
    prevEmployeeSummaryDataLengthRef.current = currentLength;
  }, [employeeSummaryData?.length]);

  // Init selectedBUsForBarGraph when available BUs change
  useEffect(() => {
    if (availableBUsForBarGraph.length === 0) return;
    if (!hasInitializedBarGraphRef.current && selectedBUsForBarGraph.length === 0) {
      hasInitializedBarGraphRef.current = true;
      setSelectedBUsForBarGraph(availableBUsForBarGraph);
    } else if (selectedBUsForBarGraph.length > 0) {
      const validBUs = selectedBUsForBarGraph.filter((bu) => availableBUsForBarGraph.includes(bu));
      if (validBUs.length > 0) {
        if (validBUs.length !== selectedBUsForBarGraph.length) setSelectedBUsForBarGraph(validBUs);
      } else {
        setSelectedBUsForBarGraph(availableBUsForBarGraph);
      }
    } else {
      setSelectedBUsForBarGraph(availableBUsForBarGraph);
    }
  }, [availableBUsForBarGraph]);

  // Init selectedBUsForTierDistribution when available BUs change
  useEffect(() => {
    if (availableBUsForTierDistribution.length === 0) return;
    if (!hasInitializedTierDistributionRef.current && selectedBUsForTierDistribution.length === 0) {
      hasInitializedTierDistributionRef.current = true;
      setSelectedBUsForTierDistribution(availableBUsForTierDistribution);
    } else if (selectedBUsForTierDistribution.length > 0) {
      const validBUs = selectedBUsForTierDistribution.filter((bu) =>
        availableBUsForTierDistribution.includes(bu)
      );
      if (validBUs.length > 0) {
        if (validBUs.length !== selectedBUsForTierDistribution.length) setSelectedBUsForTierDistribution(validBUs);
      } else {
        setSelectedBUsForTierDistribution(availableBUsForTierDistribution);
      }
    } else {
      setSelectedBUsForTierDistribution(availableBUsForTierDistribution);
    }
  }, [availableBUsForTierDistribution]);

  // Init selectedProductsForTierDistribution
  useEffect(() => {
    if (hasUserManuallySelectedProductsRef.current) return;
    if (availableProductsForTierDistribution.length === 0) return;
    if (
      !hasInitializedProductsForTierDistributionRef.current &&
      selectedProductsForTierDistribution.length === 0 &&
      !hasBeenManuallyClearedTierDistributionProductsRef.current
    ) {
      setSelectedProductsForTierDistribution(availableProductsForTierDistribution);
      hasInitializedProductsForTierDistributionRef.current = true;
    } else if (selectedProductsForTierDistribution.length > 0) {
      const validProducts = selectedProductsForTierDistribution.filter((p) =>
        availableProductsForTierDistribution.includes(p)
      );
      if (validProducts.length > 0) {
        if (validProducts.length !== selectedProductsForTierDistribution.length) {
          setSelectedProductsForTierDistribution(validProducts);
        }
      } else {
        setSelectedProductsForTierDistribution(availableProductsForTierDistribution);
      }
    } else {
      setSelectedProductsForTierDistribution(availableProductsForTierDistribution);
    }
  }, [availableProductsForTierDistribution, selectedProductsForTierDistribution.length]);

  const availableProductsKey = useMemo(() => [...availableProductsFromApi].sort().join(','), [availableProductsFromApi]);
  const trendChartInitKey = useMemo(() => {
    const rows = weeklyTrendsRaw ?? [];
    const weeks = Array.from(
      new Set(rows.map((r) => r.week_start_date).filter((w): w is string => Boolean(w)))
    ).sort();
    const latest = weeks[weeks.length - 1] ?? '';
    return `${availableProductsKey}|${latest}|${rows.length}`;
  }, [weeklyTrendsRaw, availableProductsKey]);

  // Default: top 5 products by average DRI on the latest week with data (user can change via product filter)
  useEffect(() => {
    if (isLoadingWeeklyTrends) return;
    if (trendChartInitKey === lastTrendChartInitKeyRef.current && lastTrendChartInitKeyRef.current !== '') {
      return;
    }
    const defaultSelection = getTopNProductsByLatestWeek(weeklyTrendsRaw);
    setSelectedProductsForTrendChart(defaultSelection);
    lastTrendChartInitKeyRef.current = trendChartInitKey;
  }, [trendChartInitKey, isLoadingWeeklyTrends, weeklyTrendsRaw]);

  return (
    <>
      {/* Row 1: Employee Tier Distribution and Employees by Tier */}
      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} md={6}>
          <EmployeeTierDistributionCard
            tierDistribution={filteredTierDistribution}
            availableBUs={availableBUsForTierDistribution}
            selectedBUs={selectedBUsForTierDistribution}
            onSelectedBUsChange={setSelectedBUsForTierDistribution}
            pendingBUs={pendingBUsForTierDistribution}
            onPendingBUsChange={setPendingBUsForTierDistribution}
            buMenuAnchor={buTierDistributionMenuAnchor}
            onBuMenuOpen={setBuTierDistributionMenuAnchor}
            onBuMenuClose={() => {
              setPendingBUsForTierDistribution(selectedBUsForTierDistribution);
              setBuTierDistributionMenuAnchor(null);
            }}
            onBUClearAll={() => { hasBeenManuallyClearedTierDistributionRef.current = true; }}
            onBUApply={(value) => {
              if (value.length === 0) hasBeenManuallyClearedTierDistributionRef.current = true;
            }}
            availableProducts={availableProductsForTierDistribution}
            selectedProducts={selectedProductsForTierDistribution}
            onSelectedProductsChange={setSelectedProductsForTierDistribution}
            pendingProducts={pendingProductsForTierDistribution}
            onPendingProductsChange={setPendingProductsForTierDistribution}
            productMenuAnchor={productTierDistributionMenuAnchor}
            onProductMenuOpen={setProductTierDistributionMenuAnchor}
            onProductMenuClose={() => {
              setPendingProductsForTierDistribution(selectedProductsForTierDistribution);
              setProductTierDistributionMenuAnchor(null);
            }}
            onProductClearAll={() => { hasBeenManuallyClearedTierDistributionProductsRef.current = true; }}
            onProductApply={(value) => {
              hasUserManuallySelectedProductsRef.current = true;
              if (value.length === 0) hasBeenManuallyClearedTierDistributionProductsRef.current = true;
              else hasBeenManuallyClearedTierDistributionProductsRef.current = false;
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <EmployeesByTierBarChart
            data={employeesByTierData}
            viewMode={employeesByTierViewMode}
            onViewModeChange={setEmployeesByTierViewMode}
            legendAnchorEl={legendAnchorEl}
            onLegendOpen={setLegendAnchorEl}
            onLegendClose={() => setLegendAnchorEl(null)}
          />
        </Grid>
      </Grid>

      {/* Row 2: Average DRI by Product and Weekly DRI Trends */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          {availableBUsForBarGraph.length > 0 && (
            <AverageDRIByProductBarChart
              availableBUs={availableBUsForBarGraph}
              selectedBUs={selectedBUsForBarGraph}
              onSelectedBUsChange={(value) => {
                if (value.length === 0) hasBeenManuallyClearedBarGraphRef.current = true;
                else hasBeenManuallyClearedBarGraphRef.current = false;
                setSelectedBUsForBarGraph(value);
              }}
              pendingBUs={pendingBUsForBarGraph}
              onPendingBUsChange={setPendingBUsForBarGraph}
              menuAnchor={buFilterMenuAnchor}
              onMenuOpen={setBuFilterMenuAnchor}
              onMenuClose={() => {
                setPendingBUsForBarGraph(selectedBUsForBarGraph);
                setBuFilterMenuAnchor(null);
              }}
              productDriData={productDriData}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <WeeklyDRITrendsChart
            weeklyTrendsRaw={weeklyTrendsRaw}
            availableProductsFromApi={availableProductsFromApi}
            isLoading={isLoadingWeeklyTrends}
            selectedProducts={selectedProductsForTrendChart}
            onSelectedProductsChange={setSelectedProductsForTrendChart}
            pendingProducts={pendingProductsForTrendChart}
            onPendingProductsChange={setPendingProductsForTrendChart}
            productFilterMenuAnchor={productFilterMenuAnchor}
            onProductFilterMenuAnchorChange={setProductFilterMenuAnchor}
            trendChartStartDate={trendChartStartDate}
            trendChartEndDate={trendChartEndDate}
            onTrendChartStartDateChange={setTrendChartStartDate}
            onTrendChartEndDateChange={setTrendChartEndDate}
            dateRangeMenuAnchor={dateRangeMenuAnchor}
            onDateRangeMenuAnchorChange={setDateRangeMenuAnchor}
            dateRangeTab={dateRangeTab}
            onDateRangeTabChange={setDateRangeTab}
            hoveredDataPoints={hoveredDataPoints}
            onHoveredDataPointsChange={setHoveredDataPoints}
            tooltipState={tooltipState}
            onTooltipStateChange={setTooltipState}
            hasBeenManuallyClearedRef={hasBeenManuallyClearedRef}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default DRICharts;
